import Dexie, { Table } from "dexie";
import {
  IChunkRow,
  IUploadRow,
  TChunkStatus,
  TUploadStatus,
} from "../types/db";
import {
  ITemplateRow,
  ISceneRow,
  ITelemetryRow,
  IChunkRowV5,
  IUploadRowV5,
  V5_STORES,
} from "./v5-schema";
import logger from "../utils/logger";
import {
  createBackup,
  createMigrationCheckpoint,
  saveMigrationCheckpoint,
  verifyDataIntegrity,
  clearMigrationCheckpoint,
} from "./migration-utils";

class RecorderDBV5 extends Dexie {
  chunks!: Table<IChunkRowV5, string>;
  uploads!: Table<IUploadRowV5, string>;
  templates!: Table<ITemplateRow, string>;
  scenes!: Table<ISceneRow, string>;
  telemetry!: Table<ITelemetryRow, string>;

  constructor() {
    super("DadanRecorderDB");
    
    this.version(4).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
    });

    this.version(5).stores(V5_STORES).upgrade(async (trans) => {
      logger.info("🔄 Starting IndexedDB v4→v5 migration...");
      
      try {
        const checkpoint = createMigrationCheckpoint('backup', 0, 0);
        saveMigrationCheckpoint(checkpoint);

        const v4Chunks = await trans.table('chunks').toArray();
        const v4Uploads = await trans.table('uploads').toArray();

        logger.info(`Found ${v4Chunks.length} chunks and ${v4Uploads.length} uploads to migrate`);

        checkpoint.stage = 'copy_chunks';
        checkpoint.totalRecords = v4Chunks.length + v4Uploads.length;
        saveMigrationCheckpoint(checkpoint);

        for (let i = 0; i < v4Chunks.length; i++) {
          const v4Chunk = v4Chunks[i] as any;
          const v5Chunk: IChunkRowV5 = {
            id: v4Chunk.id,
            videoGuid: v4Chunk.videoGuid,
            chunkNo: v4Chunk.chunkNo,
            status: v4Chunk.status,
            size: v4Chunk.size,
            blob: v4Chunk.blob instanceof Blob 
              ? await v4Chunk.blob.arrayBuffer() 
              : v4Chunk.blob,
            isLast: v4Chunk.isLast || false,
            uploadId: v4Chunk.uploadId,
            key: v4Chunk.key,
            createdAt: v4Chunk.createdAt,
            updatedAt: v4Chunk.updatedAt,
            templateId: undefined,
            sceneId: undefined,
          };
          await trans.table('chunks').put(v5Chunk);
          
          checkpoint.recordsProcessed = i + 1;
          if (i % 10 === 0) {
            saveMigrationCheckpoint(checkpoint);
          }
        }

        checkpoint.stage = 'copy_uploads';
        saveMigrationCheckpoint(checkpoint);

        for (let i = 0; i < v4Uploads.length; i++) {
          const v4Upload = v4Uploads[i] as any;
          const v5Upload: IUploadRowV5 = {
            videoGuid: v4Upload.videoGuid,
            status: v4Upload.status === 'aborted' || v4Upload.status === 'init-failed' 
              ? 'error' 
              : v4Upload.status,
            videoId: v4Upload.videoId,
            uploadId: v4Upload.uploadId,
            key: v4Upload.key,
            uploadedChunks: v4Upload.uploadedChunks || 0,
            parts: v4Upload.parts || [],
            createdAt: v4Upload.createdAt,
            updatedAt: v4Upload.updatedAt,
            thumbnails: [],
            syncQueued: false,
            templateId: undefined,
          };
          await trans.table('uploads').put(v5Upload);
          
          checkpoint.recordsProcessed = v4Chunks.length + i + 1;
          if (i % 10 === 0) {
            saveMigrationCheckpoint(checkpoint);
          }
        }

        checkpoint.stage = 'verify';
        saveMigrationCheckpoint(checkpoint);

        const v5Chunks = await trans.table('chunks').toArray();
        const v5Uploads = await trans.table('uploads').toArray();

        const verification = await verifyDataIntegrity(
          v4Chunks,
          v5Chunks,
          v4Uploads,
          v5Uploads
        );

        if (!verification.valid) {
          logger.error('❌ Migration verification failed:', verification.errors);
          throw new Error(`Migration verification failed: ${verification.errors.join(', ')}`);
        }

        checkpoint.stage = 'complete';
        checkpoint.recordsProcessed = checkpoint.totalRecords;
        saveMigrationCheckpoint(checkpoint);

        logger.info('✅ Migration complete:', {
          chunks: v5Chunks.length,
          uploads: v5Uploads.length,
        });

        await logTelemetry({
          eventType: 'migration_complete',
          metadata: {
            chunksCount: v5Chunks.length,
            uploadsCount: v5Uploads.length,
            duration: Date.now() - checkpoint.timestamp,
          },
        });

        setTimeout(() => clearMigrationCheckpoint(), 1000);

      } catch (error) {
        logger.error('❌ Migration failed:', error);
        
        await logTelemetry({
          eventType: 'migration_rollback',
          metadata: {
            error: error instanceof Error ? error.message : String(error),
          },
        });

        throw error;
      }
    });
  }
}

export const dbV5 = new RecorderDBV5();

async function logTelemetry(event: Partial<ITelemetryRow>) {
  try {
    const telemetry: ITelemetryRow = {
      id: `${event.eventType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      eventType: event.eventType!,
      videoGuid: event.videoGuid,
      metadata: event.metadata || {},
      timestamp: Date.now(),
      userId: event.userId,
    };
    await dbV5.telemetry.add(telemetry);
  } catch (err) {
    logger.error('Failed to log telemetry:', err);
  }
}

export async function putChunkV5(row: IChunkRowV5) {
  await dbV5.chunks.put(row);
}

export async function putUploadV5(row: IUploadRowV5) {
  await dbV5.uploads.put(row);
}

export async function getUploadV5(videoGuid: string): Promise<IUploadRowV5 | undefined> {
  return await dbV5.uploads.get(videoGuid);
}

export async function putTemplate(row: ITemplateRow) {
  await dbV5.templates.put(row);
}

export async function getTemplate(id: string): Promise<ITemplateRow | undefined> {
  return await dbV5.templates.get(id);
}

export async function getAllTemplates(): Promise<ITemplateRow[]> {
  return await dbV5.templates.toArray();
}

export async function putScene(row: ISceneRow) {
  await dbV5.scenes.put(row);
}

export async function getScenesByVideoGuid(videoGuid: string): Promise<ISceneRow[]> {
  return await dbV5.scenes.where('videoGuid').equals(videoGuid).sortBy('order');
}

export async function logTelemetryEvent(
  eventType: ITelemetryRow['eventType'],
  metadata: Record<string, any>,
  videoGuid?: string,
  userId?: string
) {
  await logTelemetry({ eventType, metadata, videoGuid, userId });
}

export async function getTelemetryByType(
  eventType: ITelemetryRow['eventType']
): Promise<ITelemetryRow[]> {
  return await dbV5.telemetry.where('eventType').equals(eventType).toArray();
}
